import { useCallback, useEffect, useMemo, useState, RefObject } from 'react';

import {
  clearQueryParamSilently,
  getQueryParam,
  setQueryParamSilently,
  sort,
} from '@cast/utils';

import { NormalizedTableProps, SortingState, TableApi } from '../../../types';
import { applySecondarySort } from '../_utils';

const serializeSort = ({ columnId, sortBy }: SortingState<any>): string => {
  const directionStr = sortBy.direction;
  const accessorStr = Array.isArray(sortBy.accessor)
    ? sortBy.accessor.join(',')
    : sortBy.accessor;
  return `${columnId}:${directionStr}${accessorStr ? `:${accessorStr}` : ''}`;
};

const applySortingType = <T>(
  api: TableApi<T>,
  sortingState?: SortingState<T>
) => {
  if (!sortingState) {
    return sortingState;
  }
  const columnModel = api.columnsInfo.columnModels.find(
    ({ id }) => id === sortingState.columnId
  );
  if (!columnModel) {
    return sortingState;
  }
  if (sortingState.sortBy.sortingType === columnModel.sortingType) {
    return sortingState;
  }
  return {
    ...sortingState,
    sortBy: {
      ...sortingState.sortBy,
      sortingType: columnModel.sortingType,
    },
  };
};

const deserializeSort = <T>(
  tableApi: TableApi<T>,
  sortStr: string
): SortingState<T> | undefined => {
  const defaultSort = tableApi.columnsInfo.defaultSortingState;
  const [columnId, direction, accessorStr] = sortStr.split(':');
  if (!(columnId && direction)) {
    return defaultSort;
  }
  const columnModel = tableApi.columnsInfo.columnModels.find(
    ({ id }) => id === columnId
  );
  if (!columnModel) {
    console.error(`Cannot find column: ${columnId}`);
    return defaultSort;
  }
  const accessor = accessorStr
    ? accessorStr.split(',')
    : Array.isArray(columnModel?.accessor)
    ? columnModel.accessor
    : [];
  return {
    columnId,
    sortBy: { direction, accessor, sortingType: columnModel.sortingType },
  } as SortingState<T>;
};

const usePersistedSort = <T>(
  tableApi: TableApi<T>,
  key?: string
): [
  SortingState<T> | undefined,
  (sortingState: SortingState<T> | undefined) => void
] => {
  const sortKey = key ? `${key}_sortBy` : undefined;
  const [sortingState, _setSortingState] = useState<
    SortingState<T> | undefined
  >(() => {
    const defaultSort = tableApi.columnsInfo.defaultSortingState;
    if (sortKey) {
      const sortParam = getQueryParam(sortKey);
      if (!sortParam) {
        return applySortingType(tableApi, defaultSort);
      }
      return deserializeSort(tableApi, sortParam) || defaultSort;
    }
    return defaultSort;
  });
  const setSortingState = (sortingState?: SortingState<T>) => {
    sortingState = applySortingType(tableApi, sortingState);
    _setSortingState(sortingState);
    if (!sortKey) {
      return;
    }
    if (!sortingState) {
      clearQueryParamSilently(sortKey);
    } else {
      setQueryParamSilently(sortKey, serializeSort(sortingState));
    }
  };
  return [sortingState, setSortingState];
};

export const useSorting = <T = any>(
  tableApi: RefObject<TableApi<T>>,
  {
    data,
    secondarySort,
    onDataSort,
    onSortingChanged,
    controlledSorting,
    urlKey,
    reorderableRows,
  }: NormalizedTableProps<T>
) => {
  const [currentSort, _setCurrentSort] = usePersistedSort(
    tableApi.current,
    urlKey
  );
  const setCurrentSort = useCallback(
    (currentSort?: SortingState<T>) => {
      onDataSort?.(currentSort);
      _setCurrentSort(currentSort);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlledSorting]
  );

  // Emit initial sorting state if sorting is controlled
  useEffect(() => {
    if (controlledSorting) {
      onDataSort?.(currentSort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledSorting]);

  const sortColumn = useCallback(
    (newSortingState?: SortingState<T>) => {
      if (!newSortingState) {
        setCurrentSort(undefined);
      } else {
        const columnModel = tableApi.current.columnsInfo.columnModels.find(
          ({ id }) => id === newSortingState.columnId
        );
        if (!columnModel) {
          throw new Error('Trying to sort non existing column');
        }
        setCurrentSort(newSortingState);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableApi, controlledSorting]
  );

  const sortFn = useCallback(
    (data: T[]) => {
      if (reorderableRows) {
        return data;
      }
      if (currentSort?.sortBy || secondarySort) {
        return sort(
          data,
          applySecondarySort(currentSort?.sortBy, secondarySort)
        );
      }
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSort, reorderableRows]
  );

  useEffect(() => {
    if (onSortingChanged) {
      onSortingChanged(sortFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortFn]);

  if (!tableApi.current?.sortColumn) {
    tableApi.current.sortColumn = sortColumn;
  }

  const { sortedData, sortingState } = useMemo(() => {
    if (controlledSorting || onSortingChanged) {
      return { sortedData: data, sortingState: currentSort };
    } else {
      const sortedData = data ? sortFn(data) : [];
      return {
        sortedData,
        sortingState: currentSort,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledSorting, data, currentSort, sortFn]);

  tableApi.current.state.sortedData = sortedData;
  tableApi.current.state.sortingState = sortingState;
};
